import { useEffect, useState } from "react";
import ConsoleLayout from "@/components/ConsoleLayout";
import { teamsApi } from "@/lib/api";
import type { WalletTransaction } from "@/lib/types";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn, formatUSD, formatSignedUSD } from "@/lib/utils";

// Wallet transaction `type` enum, mirrored from pkg/model/wallet.go
// (WalletTxType* constants). Order matches the filter dropdown.
const TYPES = ["recharge", "adjust", "payment", "voucher_recharge", "voucher_adjust", "settle", "archive_writeoff"] as const;

const TYPE_LABEL_KEY: Record<string, string> = {
  recharge: "wallet.txn.typeRecharge",
  adjust: "wallet.txn.typeAdjust",
  payment: "wallet.txn.typePayment",
  voucher_recharge: "wallet.txn.typeVoucherRecharge",
  voucher_adjust: "wallet.txn.typeVoucherAdjust",
  settle: "wallet.txn.typeSettle",
  archive_writeoff: "wallet.txn.typeArchiveWriteoff",
};

// Reuses UsageLogRow's funding-source palette (voucher/cash/credit) so a
// settle row's funding pill reads the same across the call-log and wallet-
// transaction pages.
const FUNDING_MAP: Record<string, string> = {
  voucher: "text-purple-400 bg-purple-500/10 border-purple-500/20",
  cash: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
  credit: "text-amber-400 bg-amber-500/10 border-amber-500/20",
};

const FUNDING_LABEL_KEY: Record<string, string> = {
  voucher: "logs.fundingVoucher",
  cash: "logs.fundingCash",
  credit: "logs.fundingCredit",
};

const PAGE_SIZE = 20;

// Own-team wallet transaction history (charge/adjust/settle). Backed by
// GET /api/v1/teams/:tid/wallet-transactions, gated to team membership
// server-side (claims.TeamID) — the team_id is never client-supplied, so
// unlike AdminWalletTransactions there is no team filter and no cross-team
// leakage is possible from this page.
export default function TeamWalletTransactions() {
  const { t } = useLanguage();
  const { teamId } = useAuth();

  const [typeFilter, setTypeFilter] = useState("");
  const [txns, setTxns] = useState<WalletTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const refresh = async () => {
    if (!teamId) return;
    setLoading(true);
    try {
      const res = await teamsApi.listWalletTransactions(teamId, {
        type: typeFilter || undefined,
        page,
        page_size: PAGE_SIZE,
      });
      setTxns(res.transactions);
      setTotal(res.total);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load transactions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, typeFilter, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const typeLabel = (type: string) => (TYPE_LABEL_KEY[type] ? t(TYPE_LABEL_KEY[type]) : type);

  const fmtDateTime = (s?: string) => (s ? new Date(s).toLocaleString() : "—");

  const gridCols = "grid-cols-[140px_1fr_1fr_1fr_1.2fr_120px_1.4fr]";

  return (
    <ConsoleLayout title={t("team.walletTxn.title")} subtitle={t("team.walletTxn.subtitle")}>
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPage(1);
            }}
            className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white h-9 focus:outline-none focus:border-blue-500/50 max-w-[200px]"
          >
            <option value="">{t("wallet.txn.allTypes")}</option>
            {TYPES.map((ty) => (
              <option key={ty} value={ty}>
                {typeLabel(ty)}
              </option>
            ))}
          </select>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-slate-500">
              {total} {t("logs.records")}
            </span>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-1 rounded text-slate-500 hover:text-white disabled:opacity-30"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="text-xs text-slate-400">
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="p-1 rounded text-slate-500 hover:text-white disabled:opacity-30"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="sci-card overflow-hidden">
          <div
            className={cn(
              "px-5 py-3 border-b border-white/8 grid gap-3 text-[10px] text-slate-600 uppercase tracking-wider items-center",
              gridCols,
            )}
          >
            <div>{t("wallet.txn.colTime")}</div>
            <div>{t("wallet.txn.colType")}</div>
            <div className="text-right">{t("wallet.txn.colAmount")}</div>
            <div>{t("wallet.txn.colDimension")}</div>
            <div className="text-right">{t("wallet.txn.colBalanceAfter")}</div>
            <div>{t("wallet.txn.colOperator")}</div>
            <div>{t("wallet.txn.colNote")}</div>
          </div>
          {loading ? (
            <div className="px-5 py-10 text-center text-xs text-slate-500">Loading…</div>
          ) : txns.length === 0 ? (
            <div className="px-5 py-10 text-center text-xs text-slate-500">{t("wallet.txn.empty")}</div>
          ) : (
            txns.map((txn) => {
              const fundingLabel = txn.funding_source ? t(FUNDING_LABEL_KEY[txn.funding_source] || "") : undefined;
              const isPositive = Number(txn.st_delta) > 0;
              return (
                <div
                  key={txn.id}
                  className={cn(
                    "px-5 py-3.5 border-b border-white/5 last:border-0 grid gap-3 items-center hover:bg-white/3 transition-colors",
                    gridCols,
                  )}
                >
                  <div className="text-[10px] text-slate-500">{fmtDateTime(txn.created_at)}</div>
                  <div className="text-xs text-slate-300">{typeLabel(txn.type)}</div>
                  <div
                    className={cn(
                      "text-xs text-right font-mono",
                      isPositive ? "text-emerald-400" : "text-red-400",
                    )}
                  >
                    {formatSignedUSD(txn.st_delta)}
                  </div>
                  <div>
                    {fundingLabel ? (
                      <span
                        className={cn(
                          "text-[9px] px-1.5 py-0.5 rounded-full border font-medium",
                          FUNDING_MAP[txn.funding_source!],
                        )}
                      >
                        {fundingLabel}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-600">—</span>
                    )}
                  </div>
                  <div className="text-xs text-right font-mono text-slate-300">
                    {formatUSD(txn.st_after)}
                    {(txn.type === "voucher_recharge" || txn.type === "voucher_adjust" || txn.funding_source === "voucher") && txn.voucher_after !== undefined && (
                      <span className="block text-[10px] text-purple-400/80">
                        {formatUSD(txn.voucher_after)}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-500 font-mono truncate">
                    {txn.operator_name || txn.operator_user_id || "—"}
                  </div>
                  <div className="text-[10px] text-slate-500 truncate">{txn.note || "—"}</div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </ConsoleLayout>
  );
}
